import { useEffect, useState, type ReactNode } from 'react'
import { useSelector } from 'react-redux'
import type { User, UserManager } from 'oidc-client-ts'

import {
  hasTokenSelector,
  useGetAuthenticationConfigQuery,
  useSessionOidcAuthenticationMutation
} from '../../state'
import { getIdentityHomePath, identityPaths } from '../../shared/core'
import { usePlatform } from '../../platform'
import type { IPlatformIdentityServices } from '../../platform'
import type IPlatformServicesWebOnly from './platform/iplatform-service-web-only'
import { captureOidcCallback } from './oidc-callback'

interface OidcAuthProviderProps {
  children: ReactNode
}

type GateState =
  | { status: 'open' }
  | { status: 'exchanging' }
  | { status: 'failed'; message: string }

/**
 * Completes a browser OIDC ("Authenticate with Windows") sign-in.
 *
 * Web sign-in is a full-page redirect, so the interesting work happens on
 * the page load that lands back on `redirect_uri`:
 *
 *   1. `captureOidcCallback()` takes the `code`/`state` off the URL during
 *      the first render pass — before any route guard effect can navigate to
 *      `/auth/login` and throw the code away.
 *   2. Children are held back while the callback is in flight, so the guards
 *      never run against a half-authenticated app.
 *   3. `userManager.signinCallback()` redeems the code with the IdP, and the
 *      resulting tokens are exchanged for an Axilon session token.
 *   4. `authenticationLoginSuccess` (dispatched by the mutation's side
 *      effect) persists that token and reloads, so the app reboots
 *      authenticated. That reload is why there is no success branch here.
 *
 * On a normal page load there is no callback to redeem. If the IdP session
 * is still valid but the Axilon session is not — an expired or cleared
 * session token — the stored OIDC user is exchanged for a fresh one in the
 * background, without blocking the app or bouncing through the IdP.
 *
 * When neither applies the component is inert and renders children directly,
 * so it is safe to mount unconditionally at the app root.
 */
const OidcAuthProvider = ({ children }: OidcAuthProviderProps) => {
  // Render-body capture (not an effect): guards navigate from effects, which
  // run only after every render body in the tree has executed.
  const [callbackUrl] = useState(captureOidcCallback)
  const [gate, setGate] = useState<GateState>(
    callbackUrl ? { status: 'exchanging' } : { status: 'open' }
  )
  const { data: authConfig, isError: isAuthConfigError } = useGetAuthenticationConfigQuery()
  const [exchangeOidcTokens] = useSessionOidcAuthenticationMutation()
  const hasToken = useSelector(hasTokenSelector)
  const platform = usePlatform<IPlatformIdentityServices & IPlatformServicesWebOnly>()

  const oidcEnabled = Boolean(authConfig?.oidc_web?.enabled)
  // Built in the render body, unconditionally — NOT inside the effect below.
  // `getOrCreateUserManager` memoizes the instance for the whole page, and
  // sign-out reaches for it with no config to fall back on
  // (`windowsAuth.signout()`). If we only built it on the paths that need it
  // here, an already-signed-in page load would never construct one, sign-out
  // would silently skip `removeUser()`, and the leftover OIDC user would be
  // exchanged straight back into a session on the next load.
  const userManager = platform.getOrCreateUserManager(authConfig?.oidc_web)

  useEffect(() => {
    // The gate is only ever closed by a captured callback, so it is also the
    // only thing that can be stranded by a config we can never act on.
    if (callbackUrl && (isAuthConfigError || (authConfig && !oidcEnabled))) {
      setGate({
        status: 'failed',
        message: 'Windows sign-on is not available right now. Please try again.'
      })
      return
    }

    if (!authConfig || !oidcEnabled) {
      return
    }
    // Nothing to do: no callback to redeem and the session is already live.
    if (!callbackUrl && hasToken) {
      return
    }

    let cancelled = false

    const exchange = async (user: User | null | undefined): Promise<boolean> => {
      if (!user?.id_token || !user.access_token) {
        return false
      }
      await exchangeOidcTokens({
        idToken: user.id_token,
        accessToken: user.access_token,
        client: 'web'
      }).unwrap()
      return true
    }

    const redeemCallback = async (manager: UserManager, url: string) => {
      try {
        const user = await manager.signinCallback(url)
        if (!user?.id_token || !user.access_token) {
          throw new Error('The identity provider did not return the expected tokens')
        }
        // `redirect_uri` is an IdP setting and need not be a route this app
        // serves (e.g. `/auth/callback`). Move to the post-login landing page
        // BEFORE the exchange, so the reload it triggers lands somewhere
        // routable instead of on a 404.
        window.history.replaceState({}, document.title, getIdentityHomePath())

        await exchange(user)
        // Success: the login-success listener reloads the page. Keep the
        // gate closed until it does.
      } catch (error) {
        platform.logger.error('OIDC sign-in failed', { error })
        if (!cancelled) {
          setGate({
            status: 'failed',
            message: 'We could not complete your Windows sign-in. Please try again.'
          })
        }
      }
    }

    // Best-effort and deliberately non-blocking: a missing or stale stored
    // user just means the app renders as signed out.
    const restoreStoredSession = async (manager: UserManager) => {
      try {
        const user = await manager.getUser()
        if (!user || user.expired) {
          return
        }
        await exchange(user)
      } catch (error) {
        platform.logger.warn('Could not restore session from the stored OIDC user', { error })
      }
    }

    if (!userManager) {
      if (callbackUrl) {
        setGate({
          status: 'failed',
          message: 'Windows sign-on is not configured for this deployment.'
        })
      }
      return
    }

    if (callbackUrl) {
      redeemCallback(userManager, callbackUrl)
    } else {
      restoreStoredSession(userManager)
    }

    return () => {
      cancelled = true
    }
    // `platform` is a stable module-level service object; re-running on its
    // identity would re-redeem an already-consumed authorization code.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    callbackUrl,
    authConfig,
    isAuthConfigError,
    oidcEnabled,
    hasToken,
    userManager,
    exchangeOidcTokens
  ])

  if (gate.status === 'open') {
    return children
  }

  // Rendered above the host app's theme provider, so it carries its own
  // minimal, theme-agnostic styling rather than design-system classes.
  return (
    <div
      style={{
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'system-ui, sans-serif',
        gap: 16,
        justifyContent: 'center',
        minHeight: '100vh',
        padding: 24,
        textAlign: 'center'
      }}
    >
      {gate.status === 'exchanging' ? (
        <p>Signing you in…</p>
      ) : (
        <>
          <p>{gate.message}</p>
          <button
            onClick={() => window.location.replace(identityPaths.authentication.login)}
            type="button"
          >
            Back to sign in
          </button>
        </>
      )}
    </div>
  )
}

export default OidcAuthProvider
